// import React from 'react';
// import {
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   CartesianGrid,
//   Tooltip,
//   ResponsiveContainer,
// } from 'recharts';
// import { Box } from '@mui/material';

// // --- 1. Define Chart Data ---
// // The bars represent two components: a base value (dark purple) and an upper value (light purple).
// const REVENUE_CHART_DATA = [
//   // Estimated visual values based on the chart grid lines (0K, 5K, 10K, ..., 30K)
//   // value1 = Dark Purple base
//   // value2 = Light Purple (the total bar height)
//   { day: 'MON', value1: 3000, value2: 5000 },
//   { day: 'TUE', value1: 8000, value2: 15000 },
//   { day: 'WED', value1: 5000, value2: 13000 },
//   { day: 'THU', value1: 3000, value2: 7000 },
//   { day: 'FRI', value1: 4000, value2: 8000 },
//   { day: 'SAT', value1: 9000, value2: 14000 },
//   { day: 'SUN', value1: 6000, value2: 9000 },
// ];

// // --- 2. Chart Design Component ---
// const DailyRevenueBarChart: React.FC = () => {
//   // Define colors based on the image
//   const darkPurple = '#4A3AFF'; // Darker base color
//   const lightPurple = '#E5E5EF'; // Lighter color for the upper part

//   return (
//     <Box sx={{ height: 350 }}> {/* Ensure chart container has a defined height */}
//       <ResponsiveContainer width="100%" height="100%">
//         <BarChart
//           data={REVENUE_CHART_DATA}
//           margin={{ top: 20, right: 20, left: 0, bottom: 5 }}
//           barCategoryGap="40%" // Controls the spacing between the bars
//           barGap={"10px"} // Ensure no gap between the two stacked layers
//         >
//           {/* Grid Lines: Horizontal lines only, light grey/dashed */}
//           <CartesianGrid
//             vertical={false}
//             strokeDasharray="3 3"
//             stroke="#e0e0e0"
//           />

//           {/* X-Axis (Days: MON, TUE, etc.) */}
//           <XAxis
//             dataKey="day"
//             axisLine={false} // Hide the main axis line
//             tickLine={false} // Hide the tick marks
//             style={{ fontWeight: 600, fontSize: 12 }} // Match font style
//             padding={{ left: 10, right: 10 }}
//           />

//           {/* Y-Axis (Values: 5K, 10K, etc.) */}
//           <YAxis
//             axisLine={false}
//             tickLine={false}
//             domain={[0, 30000]} // Max value is 30K
//             tickCount={7} // Show 7 ticks (0K, 5K, 10K, 15K, 20K, 25K, 30K)
//             tickFormatter={(value) => `${value / 1000}K`}
//             style={{ fontSize: 12, color: '#999' }}
//           />

//           {/* Tooltip on hover */}
//           <Tooltip />

//           {/* BAR CONFIGURATION:
//             We use two <Bar> components with the same stackId="a" to create the stacked look.
//             value1 (Dark Purple) is the bottom/base layer.
//             value2 (Light Purple) represents the *total* height of the bar.
            
//             NOTE: If you want two distinct revenue streams (e.g., Online vs. Offline), 
//             you would need to adjust the data structure so value2 is the second component 
//             that is added *on top* of value1. 
//             However, the image shows the dark purple as the lower part of the total bar.
            
//             Let's adjust the data model for standard stacking:
//             dataKey="value1" (Dark Purple) = Base
//             dataKey="value2_only" (Light Purple) = The amount to stack on top of value1
//           */}
          
//           {/* Base Layer (Dark Purple - represents one revenue stream or "achieved") */}
//           <Bar
//             dataKey="value1"
//             stackId="a"
//             fill={darkPurple}
//             radius={[2, 2, 0, 0]} // Slight rounding only on the top corners of the dark segment
            
//           />

//           {/* Stacked Layer (Light Purple - represents the rest of the revenue or "target") */}
//           {/* Note: In the chart, the light purple seems to be the total, and dark purple is the filled amount.
//              If we strictly follow the stacking convention (Bar 1 + Bar 2 = Total), the data should be:
//              value1 (Dark) and value2_only (Light portion)
//           */}
//           <Bar
//             dataKey="value2" // Using value2 as the second data key for the stacking visual
//             stackId="a"
//             fill={lightPurple}
//             radius={[5, 5, 0, 0]} // Rounding on the top of the final segment
//           />
//         </BarChart>
//       </ResponsiveContainer>
//     </Box>
//   );
// };

// export default DailyRevenueBarChart;





import React, { useState, useEffect } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { Box } from '@mui/material';
import http from "../../../commonFunctions/httpClient";
import { getLocalValue, LoacalVariables } from "../../../commonFunctions/CommonFunctions";

interface DailyRevenue {
  day: string;
  amount: number;
}

const DailyRevenueBarChart: React.FC = () => {
  const [chartData, setChartData] = useState<DailyRevenue[]>([]);
  const [loading, setLoading] = useState(true);
  const [labId, setLabId] = useState<string>("");

  const DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

  // STEP 1: Get labId
  useEffect(() => {
    const userId = getLocalValue(LoacalVariables.UserId);
    console.log("🔑 LAB ID:", userId);
    setLabId(userId || "");
  }, []);

  // STEP 2: Fetch data with labId query param
  useEffect(() => {
    if (!labId) return;

    const fetchDailyRevenue = async () => {
      try {
        setLoading(true);
        // ✅ FIXED: Added labId query parameter
        const response: any = await http.get(`lab/LabPayment/daily?labId=${labId}`);
        console.log("📈 API Response:", response);
        
        let apiData: DailyRevenue[] = [];
        if (response?.data && Array.isArray(response.data)) {
          apiData = response.data;
        } else if (Array.isArray(response)) {
          apiData = response;
        }

        console.log("📊 RAW DATA:", apiData); // ✅ Should show TUE:360, WED:478

        const apiMap = new Map();
        apiData.forEach(item => {
          const shortDay = item.day.substring(0, 3).toUpperCase();
          apiMap.set(shortDay, item.amount);
        });

        const completeData = DAYS.map(day => ({
          day,
          amount: apiMap.get(day) || 0,
        }));

        console.log("✅ CHART DATA:", completeData);
        setChartData(completeData);
      } catch (error) {
        console.error("💥 Error:", error);
        setChartData(DAYS.map(day => ({ day, amount: 0 })));
      } finally {
        setLoading(false);
      }
    };

    fetchDailyRevenue();
  }, [labId]);

  const darkPurple = '#4A3AFF';

  if (loading) {
    return (
      <Box sx={{ height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>Loading chart...</div>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 350, width: '100%' }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} barSize={50}>
          <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis 
            dataKey="day" 
            axisLine={false} 
            tickLine={false} 
            tick={{ fontSize: 12, fontWeight: 600 }}
          />
          <YAxis 
            axisLine={false} 
            tickLine={false} 
            domain={[0, 'auto']} 
            tickFormatter={(v: number) => `₹${Math.round(v / 1000)}K`}
            tick={{ fontSize: 12 }}
          />
          <Tooltip formatter={(value: number) => [`₹${value.toLocaleString()}`, 'Revenue']} />
          <Bar dataKey="amount" fill={darkPurple} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default DailyRevenueBarChart;
